import {
  Badge,
  Button,
  Card,
  Title2,
  Tooltip,
} from '@fluentui/react-components';
import { bundleIcon, Copy16Filled, Copy16Regular } from '@fluentui/react-icons';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';

import type { EnvironmentFooterProps } from './EnvironmentFooter.interface';
import { useStyles } from './EnvironmentFooter.style';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;

function CopyButton({ value, label }: { value: string; label: string }) {
  const styles = useStyles();
  const { copied, copy } = useCopyToClipboard();

  return (
    <Tooltip
      content={copied ? 'Copied!' : `Copy ${label}`}
      relationship="label"
    >
      <Button
        className={styles.copyBtn}
        appearance="subtle"
        size="small"
        icon={<CopyIcon />}
        onClick={() => copy(value)}
        aria-label={`Copy ${label} to clipboard`}
      />
    </Tooltip>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  copyable?: boolean;
}

function DetailRow({ label, value, copyable = true }: DetailRowProps) {
  const styles = useStyles();
  if (!value) return null;
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <Tooltip content={value} relationship="description">
        <span className={styles.detailValue}>{value}</span>
      </Tooltip>
      {copyable && <CopyButton value={value} label={label} />}
    </div>
  );
}

export function EnvironmentFooter({ data }: EnvironmentFooterProps) {
  const styles = useStyles();
  const env = data.environment;

  return (
    <Card className={styles.root}>
      <Title2 className={styles.header}>Environment & Metadata</Title2>

      {/* Status + Signal Type badges */}
      {(data.status || data.signalType) && (
        <div className={styles.badgeRow}>
          <span className={styles.badgeRowLabel}>Signal Info</span>
          {data.status && (
            <Badge appearance="tint" color="informative">
              {data.status}
            </Badge>
          )}
          {data.signalType && (
            <Badge appearance="tint" color="brand">
              {data.signalType}
            </Badge>
          )}
        </div>
      )}

      <div className={styles.topGrid}>
        <div className={styles.topItem}>
          <span className={styles.topLabel}>Deploy Ring</span>
          <span className={styles.topValue}>
            {env.deployRing ? (
              <Badge appearance="tint" color="informative">
                {env.deployRing}
              </Badge>
            ) : (
              '\u2014'
            )}
          </span>
        </div>
        <div className={styles.topItem}>
          <span className={styles.topLabel}>Region</span>
          <span className={styles.topValue}>{env.region || '\u2014'}</span>
        </div>
        <div className={styles.topItem}>
          <span className={styles.topLabel}>Source Branch</span>
          <span className={styles.topValue}>
            {env.sourceBranch || '\u2014'}
          </span>
        </div>
        <div className={styles.topItem}>
          <span className={styles.topLabel}>Role Instance</span>
          <span className={styles.topValue}>
            {env.roleInstance || '\u2014'}
          </span>
        </div>
      </div>

      <div className={styles.detailList}>
        <DetailRow label="Correlation ID" value={data.correlationId} />
        <DetailRow label="Server Session" value={data.serverSessionId} />
        <DetailRow label="Conversation ID" value={data.conversationId} />
        <DetailRow label="Client Session" value={data.clientSessionId} />
        <DetailRow label="Item ID" value={data.itemId} />
        <DetailRow label="LLM Scenario ID" value={data.llmScenarioId} />
        <DetailRow label="LLM Scenario" value={data.llmScenarioName} />
        <DetailRow
          label="Client Platform"
          value={data.clientPlatform}
          copyable={false}
        />
        <DetailRow
          label="Client App"
          value={data.clientAppName}
          copyable={false}
        />
        <DetailRow label="App Scenario" value={data.appScenario} />
        <DetailRow label="Actor ID" value={data.actorId} />
        <DetailRow label="Tenant ID" value={data.tenantId} />
        <DetailRow label="App ID" value={data.appId} />
        <DetailRow label="User Agent" value={data.userAgent} />
      </div>
    </Card>
  );
}
